/**
 * Awaiting-action queue page (route `/awaiting-action`).
 *
 * Spec §10.1: "Flat list of entities that have just completed a stage,
 * grouped by the next stage."
 *
 * Data: `GET /api/visibility/awaiting-action` returns a flat list of
 * action items (batches at stages 1-5 + parts at stages 6-18). We group
 * here on the client by `nextStageNumber` and render each group as a
 * section with a header showing the stage label and the count.
 *
 * Each row links to the entity drilldown (`/projects/:id/items/:itemId`)
 * so the user can pick up the action immediately.
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Box, Clock, FileText, Package2, Search, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/form-field';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Spinner } from '@/components/ui/spinner';
import { usePageTitle } from '@/lib/use-page-title';
import {
  useAwaitingAction,
  type AwaitingActionItem,
} from '@/lib/visibility-queries';

export function AwaitingActionPage() {
  usePageTitle('Awaiting action');
  const query = useAwaitingAction();

  // Patch P14 — client-side filters. The endpoint returns a flat list and
  // the page can get long (200+ items across 30+ projects). Two filters
  // cover the common questions: "show me one project" and "find that
  // batch from Supplier X." All filtering happens in memory — the data is
  // already loaded.
  const [search, setSearch] = useState('');
  const [filterProjectId, setFilterProjectId] = useState<string | null>(null);

  const rawItems = query.data?.items ?? [];

  const projectOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const it of rawItems) {
      if (!seen.has(it.projectId)) {
        seen.set(it.projectId, `${it.projectNumber} — ${it.projectTitle}`);
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rawItems]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rawItems.filter((it) => {
      if (filterProjectId && it.projectId !== filterProjectId) return false;
      if (q) {
        const haystack = [
          it.itemName,
          it.projectNumber,
          it.projectTitle,
          it.supplierName ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rawItems, search, filterProjectId]);

  const grouped = useMemo(() => groupByNextStage(filteredItems), [filteredItems]);

  const hasFilters = search.trim().length > 0 || filterProjectId !== null;
  const clearAll = () => {
    setSearch('');
    setFilterProjectId(null);
  };

  return (
    <>
      <PageHeader
        title="Awaiting action"
        subtitle="Entities ready for their next pipeline stage, grouped by what comes next. Most stale first."
      />

      {query.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner className="h-6 w-6 text-secondary" />
        </div>
      ) : query.error ? (
        <Card>
          <p className="text-sm text-danger-fg">Failed to load. {query.error.message}</p>
        </Card>
      ) : (
        <>
          <Card className="mb-3 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex w-64 items-center">
                <Search
                  className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tertiary"
                  strokeWidth={1.5}
                />
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search items, projects, suppliers…"
                  className="pl-7"
                />
              </div>
              <SearchableSelect
                items={projectOptions}
                value={filterProjectId}
                onChange={setFilterProjectId}
                placeholder="Any project"
                clearable
                className="min-w-[240px]"
              />
              {hasFilters ? (
                <button
                  type="button"
                  onClick={clearAll}
                  className="ml-auto inline-flex items-center gap-1 text-xs text-secondary hover:text-primary"
                >
                  <X className="h-3 w-3" strokeWidth={1.5} />
                  Clear
                </button>
              ) : null}
              <span className={hasFilters ? 'text-xs text-secondary' : 'ml-auto text-xs text-secondary'}>
                {filteredItems.length} of {rawItems.length}
              </span>
            </div>
          </Card>

          {grouped.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-6 w-6" strokeWidth={1.5} />}
              title={hasFilters ? 'No matches' : 'Nothing waiting'}
              body={
                hasFilters
                  ? 'No items match the current filters. Try clearing them.'
                  : 'Every entity in every open project has either reached a terminal stage or is still in pre-aggregate-gate territory.'
              }
            />
          ) : (
            <div className="space-y-4">
              {grouped.map(({ nextStageNumber, nextStageLabel, items }) => (
                <Card key={nextStageNumber}>
                  <div className="mb-2 flex items-center justify-between border-b border-border-default pb-2">
                <h3 className="text-sm font-semibold text-primary">
                  <span className="mr-2 inline-flex h-6 min-w-[24px] items-center justify-center rounded-md bg-subtle px-1.5 text-xs font-mono text-secondary">
                    {nextStageNumber}
                  </span>
                  {nextStageLabel}
                </h3>
                <span className="text-xs text-secondary">
                  {items.length} {items.length === 1 ? 'item' : 'items'}
                </span>
              </div>
              <ul className="divide-y divide-border-default">
                {items.map((item) => (
                  <ActionRow key={`${item.entityKind}-${item.entityId}`} item={item} />
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function ActionRow({ item }: { item: AwaitingActionItem }) {
  const Icon = item.entityKind === 'BATCH' ? Package2 : Box;
  return (
    <li>
      <Link
        to="/projects/$projectId/items/$itemId"
        params={{ projectId: item.projectId, itemId: item.projectItemId }}
        className="flex items-center gap-3 py-2 hover:bg-subtle"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-subtle text-secondary">
          <Icon className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-medium text-primary">{item.itemName}</span>
            <span className="text-xs text-secondary">
              {item.entityKind === 'BATCH' && item.supplierName
                ? `Batch from ${item.supplierName}`
                : item.entityKind === 'BATCH'
                  ? 'Batch'
                  : 'Part'}
            </span>
          </div>
          <p className="flex items-center gap-1.5 truncate text-xs text-secondary">
            <ProjectNumber number={item.projectNumber} size="sm" />
            <span>— {item.projectTitle}</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1 text-xs text-secondary">
          <Clock className="h-3 w-3" strokeWidth={1.5} />
          {item.daysInCurrentStage}d
        </div>
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

interface StageGroup {
  nextStageNumber: number;
  nextStageLabel: string;
  items: AwaitingActionItem[];
}

function groupByNextStage(items: AwaitingActionItem[]): StageGroup[] {
  const byStage = new Map<number, StageGroup>();
  for (const item of items) {
    const existing = byStage.get(item.nextStageNumber);
    if (existing) {
      existing.items.push(item);
    } else {
      byStage.set(item.nextStageNumber, {
        nextStageNumber: item.nextStageNumber,
        nextStageLabel: item.nextStageLabel,
        items: [item],
      });
    }
  }
  // Sort groups by stage number ascending — earlier stages first.
  return Array.from(byStage.values()).sort((a, b) => a.nextStageNumber - b.nextStageNumber);
}
